<template>
  <v-expansion-panels accordion tile :value="panel" :mandatory="activeLocations.length === 1">
    <v-expansion-panel v-for="location in activeLocations" :key="location._id" @change="onExpansionChange(location)">
      <v-expansion-panel-header>
        {{ location.name }}
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-card flat>
          <v-img
            v-if="location.gallery && location.gallery.length === 1 && ((location.videos && !location.videos.length) || !location.videos)"
            :src="location.gallery[0]"
            class="negative-margin" />

          <youtube
            v-else-if="location.videos && location.videos.length === 1 && ((location.gallery && !location.gallery.length) || !location.gallery) && getVideoId(location.videos[0])"
            :video-id="getVideoId(location.videos[0])"
            player-height="250px"
            player-width="100%"
            @ready="playerReady(location, 0, $event)"
            class="negative-margin" />

          <v-carousel
            v-else-if="(location.gallery && location.gallery.length > 1) || (location.videos && location.videos.length > 1)"
            v-model="location.slide"
            height="250px"
            hide-delimiters
            @change="onCarouselChange(location, $event)"
            class="negative-margin">
            <v-carousel-item v-for="(video, index) in location.videos" :key="`${index}-video`">
              <youtube
                v-if="getVideoId(video)"
                :video-id="getVideoId(video)"
                player-height="250px"
                player-width="100%"
                @ready="playerReady(location, index, $event)" />
            </v-carousel-item>
            <v-carousel-item v-for="(image, index) in location.gallery" :key="`${index}-image`">
              <v-img :src="image" height="100%" contain />
            </v-carousel-item>
          </v-carousel>

          <v-card-title primary-title class="secondary--text">
            <h3 class="headline">{{ location.name }}</h3>
          </v-card-title>
          <v-card-subtitle v-if="location.code" class="primary--text">
            {{ location.code }}
          </v-card-subtitle>
          <v-card-text v-if="location.description || alertIsLive(location)">
            <template v-if="alertIsLive(location)">
              <v-chip
                small
                color="red"
                class="mb-2"
                text-color="white">
                <v-avatar left>
                  <v-icon small>fa-exclamation</v-icon>
                </v-avatar>
                Alert Information
              </v-chip>
              <div v-if="alertIsLive(location)" class="error--text">{{ location.alert.text }}</div>
            </template>

            <hr
              v-if="alertIsLive(location) && location.description"
              class="negative-margin my-2" />

            <div v-if="location.description">{{ location.description }}</div>
          </v-card-text>

          <v-card-actions class="pt-0">
            <v-row>
              <v-col class="text-center pa-0">
                <v-btn
                  fab outlined small
                  color="secondary"
                  @click="$router.push({ name: 'directions', params: { id: location._id }})"
                >
                  <v-icon dense>fa-map-signs</v-icon>
                </v-btn>
                <div class="caption secondary--text">Directions</div>
              </v-col>

              <v-col class="text-center pa-0" v-if="location.spaces && location.spaces.length">
                <v-btn
                  fab outlined small
                  color="secondary"
                  @click="$router.push({ params: { mode: 'events' } })"
                >
                  <v-icon dense>fa-calendar</v-icon>
                </v-btn>
                <div class="caption secondary--text">Events</div>
              </v-col>

              <v-col class="text-center pa-0" v-if="location.tour && getTourLink(location)">
                <v-btn
                  fab outlined small
                  color="secondary"
                  :href="getTourLink(location)" target="_blank"
                >
                  <v-icon dense>fa-camera</v-icon>
                </v-btn>
                <div class="caption secondary--text">Tour</div>
              </v-col>

              <v-col class="text-center pa-0" v-if="location.accessible">
                <v-btn
                  fab outlined small
                  color="secondary"
                >
                  <v-icon dense>fa-wheelchair</v-icon>
                </v-btn>
                <div class="caption secondary--text">{{ location.accessible }} Accessibility</div>
              </v-col>

              <v-col class="text-center pa-0" v-if="location.code && departments.length">
                <v-btn
                  fab outlined small
                  color="secondary"
                  @click="$router.push({ params: { mode: 'departments' } })"
                >
                  <v-icon dense>fa-users</v-icon>
                </v-btn>
                <div class="caption secondary--text">Departments</div>
              </v-col>

              <v-col
                class="text-center pa-0"
                v-if="location.hours && hoursExist(location.hours)"
              >
                <v-btn
                  fab outlined small
                  :color="getHoursColor(location.availability)"
                  @click="$router.push({ params: { mode: 'hours' } })"
                >
                  <v-icon dense>fa-clock</v-icon>
                </v-btn>
                <div class="caption secondary--text">Hours</div>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { DateTime } from 'luxon'

import mobile from '@/mixins/mobile'

export default {
  mixins: [
    mobile
  ],
  name: 'Results',
  data () {
    return {
      panel: []
    }
  },
  computed: {
    ...mapGetters([
      'activeLocations',
      'selectedLocation',
      'departments',
      'currentDepartmentsCode'
    ])
  },
  watch: {
    async activeLocations () {
      this.activeLocations.forEach(location => {
        if (location.hours) {
          const currentDate = new Date()
          if (location.hours[this.getCurrentDay()]) {
            const open = DateTime.fromFormat(location.hours[this.getCurrentDay()].open, 'hh:mm')
            let close = DateTime.fromFormat(location.hours[this.getCurrentDay()].close, 'hh:mm')

            if (close < open) {
              close = close.plus({ days: 1 })
            }

            location.availability = ((currentDate >= open && currentDate <= close) ? 'open' : 'closed')
          }
        }
      })

      if (this.activeLocations.length === 1) {
        this.panel = 0
        this.onExpansionChange(this.activeLocations[0])
      }
    },
    selectedLocation () {
      if (this.selectedLocation && this.selectedLocation.code && (this.currentDepartmentsCode !== this.selectedLocation.code)) {
        this.loadDepartments(this.selectedLocation.code)
      }
    }
  },
  methods: {
    ...mapActions([
      'setActiveLocations',
      'loadDepartments'
    ]),
    ...mapMutations([
      'setSelectedLocation'
    ]),
    getCurrentDay () {
      const currentDate = new Date()
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

      return days[currentDate.getDay()]
    },
    alertIsLive (location) {
      const now = new Date()

      if (location.alert) {
        const start = new Date(location.alert.start)
        const end = new Date(location.alert.end)

        return (start <= now && end > now)
      }

      return false
    },
    getTourLink (location) {
      if (location && location.tour && this.isMobile()) {
        return location.tour.mobile
      } else if (location && location.tour && !this.isMobile()) {
        return location.tour.desktop
      }

      return false
    },
    onExpansionChange (location) {
      this.setSelectedLocation(location)
    },
    onCarouselChange (location, index) {
      if (!location.players) location.players = {}

      Object.values(location.players).forEach(player => {
        player.pauseVideo()
      })
    },
    getHoursColor (availability) {
      const map = {
        open: 'green',
        closed: 'red'
      }

      return map[availability] || 'secondary'
    },
    playerReady (location, index, event) {
      if (!location.players) location.players = {}

      location.players[index] = event.target
    },
    getVideoId (url) {
      if (!url) return
      const urlObj = new URL(url)

      return urlObj.searchParams.get('v')
    },
    hoursExist (hours) {
      return !Object.values(hours).every(day => {
        return (day.open === '' || !day.open) && (day.close === '' || !day.close)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
button.hours {
  margin: 0 0 0 -7px;

  min-width: 0;
  padding: 0 10px;
}

.v-card__actions,
.v-card__title,
.v-card__text,
.v-card__subtitle {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.v-expansion-panel {
  &::before {
    box-shadow: none;
  }
}

.v-icon.v-icon--dense {
  font-size: 20px !important;
}

.negative-margin {
  margin: 0 -24px;
  max-width: none;
  width: auto;
}
</style>
